import i18n from '../i18n';

// Type definitions
interface LocalizedData {
  departments: string[];
  jobTitles: string[];
  jobLevels: string[];
  skills: string[];
  locations: string[];
  firstNames: string[];
  lastNames: string[];
  performanceRatings: string[];
  feedbackTemplates: string[];
  projectNames: string[];
  certifications: string[];
  benefits: string[];
  trainingPrograms: string[];
}

// Localized data for all supported languages
const LOCALIZED_DATA: Record<string, LocalizedData> = {
  'en-US': {
    departments: [
      'Engineering',
      'Human Resources',
      'Sales',
      'Marketing',
      'Finance',
      'Operations',
      'Product Management',
      'Customer Success',
      'Legal',
      'Research & Development'
    ],
    jobTitles: [
      'Software Engineer',
      'Senior Developer',
      'Product Manager',
      'HR Manager',
      'Sales Representative',
      'Marketing Specialist',
      'Financial Analyst',
      'Operations Manager',
      'UX Designer',
      'Data Scientist'
    ],
    jobLevels: [
      'Entry Level',
      'Junior',
      'Mid-Level',
      'Senior',
      'Lead',
      'Principal',
      'Manager',
      'Director',
      'Vice President',
      'Executive'
    ],
    skills: [
      'Leadership',
      'Communication',
      'Problem Solving',
      'Project Management',
      'Data Analysis',
      'Strategic Planning',
      'Team Collaboration',
      'Innovation',
      'Customer Service',
      'Technical Expertise'
    ],
    locations: [
      'New York, USA',
      'San Francisco, USA',
      'London, UK',
      'Berlin, Germany',
      'Tokyo, Japan',
      'Sydney, Australia',
      'Toronto, Canada',
      'Singapore',
      'Dubai, UAE',
      'Mumbai, India'
    ],
    firstNames: [
      'John', 'Sarah', 'Michael', 'Emma', 'David',
      'Lisa', 'James', 'Jennifer', 'Robert', 'Maria'
    ],
    lastNames: [
      'Smith', 'Johnson', 'Williams', 'Brown', 'Davis',
      'Miller', 'Wilson', 'Moore', 'Taylor', 'Anderson'
    ],
    performanceRatings: [
      'Exceeds Expectations',
      'Meets Expectations',
      'Needs Improvement',
      'Outstanding',
      'Satisfactory'
    ],
    feedbackTemplates: [
      'Demonstrates strong leadership skills',
      'Excellent team collaboration',
      'Consistently delivers high-quality work',
      'Shows initiative and innovation',
      'Needs to improve time management'
    ],
    projectNames: [
      'Digital Transformation',
      'Cloud Migration',
      'Customer Portal',
      'Data Analytics Platform',
      'Mobile App Development'
    ],
    certifications: [
      'PMP Certified',
      'AWS Solutions Architect',
      'Google Cloud Professional',
      'Microsoft Azure Expert',
      'Scrum Master'
    ],
    benefits: [
      'Health Insurance',
      'Dental Coverage',
      'Vision Plan',
      'Retirement 401k',
      'Life Insurance'
    ],
    trainingPrograms: [
      'Leadership Development',
      'Technical Skills Training',
      'Diversity & Inclusion',
      'Project Management',
      'Communication Skills'
    ]
  },
  
  'de-DE': {
    departments: [
      'Entwicklung',
      'Personalwesen',
      'Vertrieb',
      'Marketing',
      'Finanzen',
      'Betrieb',
      'Produktmanagement',
      'Kundenerfolg',
      'Rechtsabteilung',
      'Forschung & Entwicklung'
    ],
    jobTitles: [
      'Softwareentwickler',
      'Senior Entwickler',
      'Produktmanager',
      'Personalleiter',
      'Vertriebsmitarbeiter',
      'Marketing-Spezialist',
      'Finanzanalyst',
      'Betriebsleiter',
      'UX-Designer',
      'Datenwissenschaftler'
    ],
    jobLevels: [
      'Einstiegslevel',
      'Junior',
      'Mittlere Ebene',
      'Senior',
      'Führung',
      'Principal',
      'Manager',
      'Direktor',
      'Vizepräsident',
      'Geschäftsführung'
    ],
    skills: [
      'Führung',
      'Kommunikation',
      'Problemlösung',
      'Projektmanagement',
      'Datenanalyse',
      'Strategische Planung',
      'Teamzusammenarbeit',
      'Innovation',
      'Kundenservice',
      'Technische Expertise'
    ],
    locations: [
      'Berlin, Deutschland',
      'München, Deutschland',
      'Frankfurt, Deutschland',
      'Hamburg, Deutschland',
      'Wien, Österreich',
      'Zürich, Schweiz',
      'Köln, Deutschland',
      'Stuttgart, Deutschland',
      'Düsseldorf, Deutschland',
      'Leipzig, Deutschland'
    ],
    firstNames: [
      'Klaus', 'Anna', 'Thomas', 'Julia', 'Markus',
      'Sandra', 'Stefan', 'Petra', 'Andreas', 'Sabine'
    ],
    lastNames: [
      'Müller', 'Schmidt', 'Schneider', 'Fischer', 'Weber',
      'Meyer', 'Wagner', 'Becker', 'Schulz', 'Hoffmann'
    ],
    performanceRatings: [
      'Übertrifft Erwartungen',
      'Erfüllt Erwartungen',
      'Verbesserungsbedarf',
      'Hervorragend',
      'Zufriedenstellend'
    ],
    feedbackTemplates: [
      'Zeigt starke Führungsqualitäten',
      'Ausgezeichnete Teamzusammenarbeit',
      'Liefert konstant hochwertige Arbeit',
      'Zeigt Initiative und Innovation',
      'Muss Zeitmanagement verbessern'
    ],
    projectNames: [
      'Digitale Transformation',
      'Cloud-Migration',
      'Kundenportal',
      'Datenanalyse-Plattform',
      'Mobile App-Entwicklung'
    ],
    certifications: [
      'PMP Zertifiziert',
      'AWS Lösungsarchitekt',
      'Google Cloud Professional',
      'Microsoft Azure Experte',
      'Scrum Master'
    ],
    benefits: [
      'Krankenversicherung',
      'Zahnversicherung',
      'Augenversicherung',
      'Altersvorsorge',
      'Lebensversicherung'
    ],
    trainingPrograms: [
      'Führungskräfteentwicklung',
      'Technische Schulung',
      'Vielfalt & Inklusion',
      'Projektmanagement',
      'Kommunikationsfähigkeiten'
    ]
  },
  
  'sv-SE': {
    departments: [
      'Utveckling',
      'Personal',
      'Försäljning',
      'Marknadsföring',
      'Ekonomi',
      'Drift',
      'Produktledning',
      'Kundframgång',
      'Juridik',
      'Forskning & Utveckling'
    ],
    jobTitles: [
      'Mjukvaruutvecklare',
      'Senior Utvecklare',
      'Produktchef',
      'HR-chef',
      'Säljrepresentant',
      'Marknadsföringsspecialist',
      'Finansanalytiker',
      'Driftschef',
      'UX-designer',
      'Datavetare'
    ],
    jobLevels: [
      'Nybörjarnivå',
      'Junior',
      'Mellannivå',
      'Senior',
      'Ledande',
      'Principal',
      'Chef',
      'Direktör',
      'Vice VD',
      'Ledning'
    ],
    skills: [
      'Ledarskap',
      'Kommunikation',
      'Problemlösning',
      'Projektledning',
      'Dataanalys',
      'Strategisk planering',
      'Teamsamarbete',
      'Innovation',
      'Kundservice',
      'Teknisk expertis'
    ],
    locations: [
      'Stockholm, Sverige',
      'Göteborg, Sverige',
      'Malmö, Sverige',
      'Uppsala, Sverige',
      'Linköping, Sverige',
      'Örebro, Sverige',
      'Västerås, Sverige',
      'Helsingborg, Sverige',
      'Jönköping, Sverige',
      'Umeå, Sverige'
    ],
    firstNames: [
      'Erik', 'Anna', 'Lars', 'Maria', 'Anders',
      'Eva', 'Per', 'Karin', 'Johan', 'Helena'
    ],
    lastNames: [
      'Andersson', 'Johansson', 'Karlsson', 'Nilsson', 'Eriksson',
      'Larsson', 'Olsson', 'Persson', 'Svensson', 'Gustafsson'
    ],
    performanceRatings: [
      'Överträffar förväntningar',
      'Uppfyller förväntningar',
      'Behöver förbättring',
      'Enastående',
      'Tillfredsställande'
    ],
    feedbackTemplates: [
      'Visar starka ledarskapsförmågor',
      'Utmärkt teamsamarbete',
      'Levererar konsekvent högkvalitativt arbete',
      'Visar initiativ och innovation',
      'Behöver förbättra tidshantering'
    ],
    projectNames: [
      'Digital Transformation',
      'Molnmigration',
      'Kundportal',
      'Dataanalysplattform',
      'Mobilapputveckling'
    ],
    certifications: [
      'PMP-certifierad',
      'AWS Lösningsarkitekt',
      'Google Cloud Professional',
      'Microsoft Azure Expert',
      'Scrum Master'
    ],
    benefits: [
      'Sjukförsäkring',
      'Tandvård',
      'Synförsäkring',
      'Pensionsplan',
      'Livförsäkring'
    ],
    trainingPrograms: [
      'Ledarskapsutveckling',
      'Teknisk utbildning',
      'Mångfald & Inkludering',
      'Projektledning',
      'Kommunikationsförmåga'
    ]
  },
  
  'da-DK': {
    departments: [
      'Udvikling',
      'Personale',
      'Salg',
      'Marketing',
      'Økonomi',
      'Drift',
      'Produktledelse',
      'Kundesucces',
      'Juridisk',
      'Forskning & Udvikling'
    ],
    jobTitles: [
      'Softwareudvikler',
      'Senior Udvikler',
      'Produktchef',
      'HR-chef',
      'Salgsrepræsentant',
      'Marketingspecialist',
      'Finansanalytiker',
      'Driftsleder',
      'UX-designer',
      'Data Scientist'
    ],
    jobLevels: [
      'Begynderniveau',
      'Junior',
      'Mellemniveau',
      'Senior',
      'Ledende',
      'Principal',
      'Leder',
      'Direktør',
      'Vicepræsident',
      'Ledelse'
    ],
    skills: [
      'Lederskab',
      'Kommunikation',
      'Problemløsning',
      'Projektledelse',
      'Dataanalyse',
      'Strategisk planlægning',
      'Teamsamarbejde',
      'Innovation',
      'Kundeservice',
      'Teknisk ekspertise'
    ],
    locations: [
      'København, Danmark',
      'Aarhus, Danmark',
      'Odense, Danmark',
      'Aalborg, Danmark',
      'Esbjerg, Danmark',
      'Randers, Danmark',
      'Kolding, Danmark',
      'Horsens, Danmark',
      'Vejle, Danmark',
      'Roskilde, Danmark'
    ],
    firstNames: [
      'Mads', 'Anne', 'Peter', 'Mette', 'Jens',
      'Lise', 'Henrik', 'Camilla', 'Thomas', 'Karen'
    ],
    lastNames: [
      'Nielsen', 'Jensen', 'Hansen', 'Pedersen', 'Andersen',
      'Christensen', 'Larsen', 'Sørensen', 'Rasmussen', 'Jørgensen'
    ],
    performanceRatings: [
      'Overgår forventninger',
      'Opfylder forventninger',
      'Behøver forbedring',
      'Fremragende',
      'Tilfredsstillende'
    ],
    feedbackTemplates: [
      'Viser stærke lederevner',
      'Fremragende teamsamarbejde',
      'Leverer konsekvent højkvalitetsarbejde',
      'Viser initiativ og innovation',
      'Skal forbedre tidsstyring'
    ],
    projectNames: [
      'Digital Transformation',
      'Cloud Migration',
      'Kundeportal',
      'Dataanalyseplatform',
      'Mobilapp-udvikling'
    ],
    certifications: [
      'PMP Certificeret',
      'AWS Løsningsarkitekt',
      'Google Cloud Professional',
      'Microsoft Azure Ekspert',
      'Scrum Master'
    ],
    benefits: [
      'Sundhedsforsikring',
      'Tandforsikring',
      'Synsforsikring',
      'Pensionsordning',
      'Livsforsikring'
    ],
    trainingPrograms: [
      'Ledelsesudvikling',
      'Teknisk træning',
      'Mangfoldighed & Inklusion',
      'Projektledelse',
      'Kommunikationsevner'
    ]
  },
  
  'fr-FR': {
    departments: [
      'Développement',
      'Ressources Humaines',
      'Ventes',
      'Marketing',
      'Finance',
      'Opérations',
      'Gestion de Produit',
      'Succès Client',
      'Juridique',
      'Recherche & Développement'
    ],
    jobTitles: [
      'Développeur Logiciel',
      'Développeur Senior',
      'Chef de Produit',
      'Responsable RH',
      'Représentant Commercial',
      'Spécialiste Marketing',
      'Analyste Financier',
      'Responsable des Opérations',
      'Designer UX',
      'Data Scientist'
    ],
    jobLevels: [
      'Niveau Débutant',
      'Junior',
      'Niveau Intermédiaire',
      'Senior',
      'Principal',
      'Principal',
      'Manager',
      'Directeur',
      'Vice-Président',
      'Direction'
    ],
    skills: [
      'Leadership',
      'Communication',
      'Résolution de Problèmes',
      'Gestion de Projet',
      'Analyse de Données',
      'Planification Stratégique',
      'Collaboration d\'Équipe',
      'Innovation',
      'Service Client',
      'Expertise Technique'
    ],
    locations: [
      'Paris, France',
      'Lyon, France',
      'Marseille, France',
      'Toulouse, France',
      'Nice, France',
      'Nantes, France',
      'Strasbourg, France',
      'Montpellier, France',
      'Bordeaux, France',
      'Lille, France'
    ],
    firstNames: [
      'Pierre', 'Marie', 'Jean', 'Sophie', 'Michel',
      'Isabelle', 'Philippe', 'Catherine', 'Alain', 'Nathalie'
    ],
    lastNames: [
      'Martin', 'Bernard', 'Dubois', 'Thomas', 'Robert',
      'Richard', 'Petit', 'Durand', 'Leroy', 'Moreau'
    ],
    performanceRatings: [
      'Dépasse les Attentes',
      'Répond aux Attentes',
      'Besoin d\'Amélioration',
      'Exceptionnel',
      'Satisfaisant'
    ],
    feedbackTemplates: [
      'Démontre de fortes compétences en leadership',
      'Excellente collaboration d\'équipe',
      'Fournit constamment un travail de haute qualité',
      'Fait preuve d\'initiative et d\'innovation',
      'Doit améliorer la gestion du temps'
    ],
    projectNames: [
      'Transformation Numérique',
      'Migration Cloud',
      'Portail Client',
      'Plateforme d\'Analyse de Données',
      'Développement d\'Application Mobile'
    ],
    certifications: [
      'Certifié PMP',
      'Architecte de Solutions AWS',
      'Google Cloud Professional',
      'Expert Microsoft Azure',
      'Scrum Master'
    ],
    benefits: [
      'Assurance Maladie',
      'Couverture Dentaire',
      'Plan Vision',
      'Plan de Retraite',
      'Assurance Vie'
    ],
    trainingPrograms: [
      'Développement du Leadership',
      'Formation Technique',
      'Diversité & Inclusion',
      'Gestion de Projet',
      'Compétences en Communication'
    ]
  },
  
  'ar': {
    departments: [
      'التطوير',
      'الموارد البشرية',
      'المبيعات',
      'التسويق',
      'المالية',
      'العمليات',
      'إدارة المنتجات',
      'نجاح العملاء',
      'القانونية',
      'البحث والتطوير'
    ],
    jobTitles: [
      'مهندس برمجيات',
      'مطور أول',
      'مدير منتج',
      'مدير الموارد البشرية',
      'ممثل مبيعات',
      'أخصائي تسويق',
      'محلل مالي',
      'مدير العمليات',
      'مصمم تجربة المستخدم',
      'عالم بيانات'
    ],
    jobLevels: [
      'مستوى مبتدئ',
      'مبتدئ',
      'مستوى متوسط',
      'متقدم',
      'قائد',
      'رئيسي',
      'مدير',
      'مدير تنفيذي',
      'نائب رئيس',
      'تنفيذي'
    ],
    skills: [
      'القيادة',
      'التواصل',
      'حل المشكلات',
      'إدارة المشاريع',
      'تحليل البيانات',
      'التخطيط الاستراتيجي',
      'التعاون الجماعي',
      'الابتكار',
      'خدمة العملاء',
      'الخبرة التقنية'
    ],
    locations: [
      'الرياض، السعودية',
      'دبي، الإمارات',
      'القاهرة، مصر',
      'جدة، السعودية',
      'الكويت، الكويت',
      'الدوحة، قطر',
      'عمّان، الأردن',
      'بيروت، لبنان',
      'المنامة، البحرين',
      'مسقط، عُمان'
    ],
    firstNames: [
      'محمد', 'فاطمة', 'أحمد', 'عائشة', 'عبدالله',
      'مريم', 'خالد', 'نورا', 'عمر', 'سارة'
    ],
    lastNames: [
      'الأحمد', 'العلي', 'الحسن', 'الخالد', 'السالم',
      'المحمد', 'الراشد', 'العبدالله', 'الناصر', 'الزهراني'
    ],
    performanceRatings: [
      'يتجاوز التوقعات',
      'يلبي التوقعات',
      'يحتاج إلى تحسين',
      'متميز',
      'مُرضي'
    ],
    feedbackTemplates: [
      'يُظهر مهارات قيادية قوية',
      'تعاون ممتاز مع الفريق',
      'يقدم باستمرار عملاً عالي الجودة',
      'يُظهر المبادرة والابتكار',
      'يحتاج إلى تحسين إدارة الوقت'
    ],
    projectNames: [
      'التحول الرقمي',
      'الانتقال إلى السحابة',
      'بوابة العملاء',
      'منصة تحليل البيانات',
      'تطوير تطبيق الجوال'
    ],
    certifications: [
      'شهادة PMP',
      'مهندس حلول AWS',
      'محترف Google Cloud',
      'خبير Microsoft Azure',
      'Scrum Master'
    ],
    benefits: [
      'التأمين الصحي',
      'تغطية الأسنان',
      'خطة الرؤية',
      'خطة التقاعد',
      'التأمين على الحياة'
    ],
    trainingPrograms: [
      'تطوير القيادة',
      'التدريب التقني',
      'التنوع والشمول',
      'إدارة المشاريع',
      'مهارات التواصل'
    ]
  }
};

// Helper function to get localized data based on current language
export function getLocalizedData(): LocalizedData {
  const currentLang = i18n.language || 'en-US';
  
  // Try exact match first
  if (LOCALIZED_DATA[currentLang]) {
    return LOCALIZED_DATA[currentLang];
  }
  
  // Try language without region (e.g., 'en' from 'en-US')
  const langCode = currentLang.split('-')[0];
  const matchingLang = Object.keys(LOCALIZED_DATA).find(
    key => key.startsWith(langCode)
  );
  
  if (matchingLang && LOCALIZED_DATA[matchingLang]) {
    return LOCALIZED_DATA[matchingLang];
  }
  
  // Fallback to English
  return LOCALIZED_DATA['en-US'];
}

// Function to generate a localized employee name
export function generateLocalizedName(): { firstName: string; lastName: string; fullName: string } {
  const data = getLocalizedData();
  const firstName = data.firstNames[Math.floor(Math.random() * data.firstNames.length)];
  const lastName = data.lastNames[Math.floor(Math.random() * data.lastNames.length)];
  
  // Handle RTL languages (Arabic)
  const isRTL = i18n.language === 'ar';
  const fullName = isRTL ? `${firstName} ${lastName}` : `${firstName} ${lastName}`;
  
  return { firstName, lastName, fullName };
}

// Function to get a random localized department
export function getRandomDepartment(): string {
  const data = getLocalizedData();
  return data.departments[Math.floor(Math.random() * data.departments.length)];
}

// Function to get a random localized job title
export function getRandomJobTitle(): string {
  const data = getLocalizedData();
  return data.jobTitles[Math.floor(Math.random() * data.jobTitles.length)];
}

// Function to get a random localized location
export function getRandomLocation(): string {
  const data = getLocalizedData();
  return data.locations[Math.floor(Math.random() * data.locations.length)];
}

// Function to get a random localized skill
export function getRandomSkills(count: number = 3): string[] {
  const data = getLocalizedData();
  const skills = [...data.skills];
  const selected: string[] = [];
  
  for (let i = 0; i < Math.min(count, skills.length); i++) {
    const index = Math.floor(Math.random() * skills.length);
    selected.push(skills.splice(index, 1)[0]);
  }
  
  return selected;
}

// Function to get localized performance rating
export function getRandomPerformanceRating(): string {
  const data = getLocalizedData();
  return data.performanceRatings[Math.floor(Math.random() * data.performanceRatings.length)];
}

// Function to get localized feedback
export function getRandomFeedback(): string {
  const data = getLocalizedData();
  return data.feedbackTemplates[Math.floor(Math.random() * data.feedbackTemplates.length)];
}

// Function to get all departments
export function getAllDepartments(): string[] {
  return getLocalizedData().departments;
}

// Function to get all job levels
export function getAllJobLevels(): string[] {
  return getLocalizedData().jobLevels;
}

// Function to get all locations
export function getAllLocations(): string[] {
  return getLocalizedData().locations;
}

// Function to get all skills
export function getAllSkills(): string[] {
  return getLocalizedData().skills;
}

// Function to get project names
export function getRandomProjectName(): string {
  const data = getLocalizedData();
  return data.projectNames[Math.floor(Math.random() * data.projectNames.length)];
}

// Function to get certifications
export function getRandomCertifications(count: number = 2): string[] {
  const data = getLocalizedData();
  const certs = [...data.certifications];
  const selected: string[] = [];
  
  for (let i = 0; i < Math.min(count, certs.length); i++) {
    const index = Math.floor(Math.random() * certs.length);
    selected.push(certs.splice(index, 1)[0]);
  }
  
  return selected;
}

// Function to get benefits
export function getRandomBenefits(): string[] {
  const data = getLocalizedData();
  return data.benefits;
}

// Function to get training programs
export function getRandomTrainingPrograms(count: number = 2): string[] {
  const data = getLocalizedData();
  const programs = [...data.trainingPrograms];
  const selected: string[] = [];
  
  for (let i = 0; i < Math.min(count, programs.length); i++) {
    const index = Math.floor(Math.random() * programs.length);
    selected.push(programs.splice(index, 1)[0]);
  }
  
  return selected;
}

// Export default for convenience
export default {
  getLocalizedData,
  generateLocalizedName,
  getRandomDepartment,
  getRandomJobTitle,
  getRandomLocation,
  getRandomSkills,
  getRandomPerformanceRating,
  getRandomFeedback,
  getAllDepartments,
  getAllJobLevels,
  getAllLocations,
  getAllSkills,
  getRandomProjectName,
  getRandomCertifications,
  getRandomBenefits,
  getRandomTrainingPrograms
};